import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Command slice — UI-only state for the command-chat experience.
 *
 * All data-layer concerns (sessions, messages, streaming) live in react-query
 * (`use-command-sessions`, `use-command-messages`, `use-command-agents`) plus the
 * component-local streaming state in chat-area. This slice owns only UI flags
 * that must survive across components without prop drilling.
 */
interface CommandState {
  activeAgentId: string | null;
  activeSessionId: string | null;
  /** Owning agent for `activeSessionId` — set when selecting from project/other chats. */
  activeSessionAgentId: string | null;
  sidebarOpen: boolean;
  selectedModel: string | null;
  typingTitle: { sessionId: string; title: string } | null;
}

const initialState: CommandState = {
  activeAgentId: null,
  activeSessionId: null,
  activeSessionAgentId: null,
  sidebarOpen: true,
  selectedModel: null,
  typingTitle: null,
};

const commandSlice = createSlice({
  name: 'command',
  initialState,
  reducers: {
    setActiveAgent(state, action: PayloadAction<string | null>) {
      const next = action.payload;
      // Re-selecting the same agent must not clear activeSessionId so URL-driven
      // session sync survives agent id re-resolution.
      if (state.activeAgentId === next) return;
      state.activeAgentId = next;
      state.activeSessionId = null;
      state.activeSessionAgentId = null;
    },
    setActiveSession(
      state,
      action: PayloadAction<{ sessionId: string | null; agentId?: string | null }>
    ) {
      const { sessionId, agentId } = action.payload;
      state.activeSessionId = sessionId;
      if (!sessionId) {
        state.activeSessionAgentId = null;
      } else if (agentId) {
        state.activeSessionAgentId = agentId;
      }
    },
    setSidebarOpen(state, action: PayloadAction<boolean>) {
      state.sidebarOpen = action.payload;
    },
    toggleSidebar(state) {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSelectedModel(state, action: PayloadAction<string | null>) {
      state.selectedModel = action.payload;
    },
    setTypingTitle(state, action: PayloadAction<{ sessionId: string; title: string } | null>) {
      state.typingTitle = action.payload;
    },
    resetCommandState(state) {
      state.activeAgentId = null;
      state.activeSessionId = null;
      state.activeSessionAgentId = null;
      state.typingTitle = null;
    },
  },
});

export const {
  setActiveAgent,
  setActiveSession,
  setSidebarOpen,
  toggleSidebar,
  setSelectedModel,
  setTypingTitle,
  resetCommandState,
} = commandSlice.actions;

export default commandSlice.reducer;
